import { logger } from "@/lib/logger"
import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import { ThresholdMetricBox } from "@/components/ThresholdMetricBox"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { parseResultJson } from "../helpers/parseResultJson"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"

const log = logger("SpeechTempoMetric")

interface SpeechTempoThreshold {
  warning: number
  danger: number
}

interface SpeechTempoResultJson {
  overall_pps: number
  statistics: {
    average_pps: number
    max_pps: number
    min_pps: number
    std_pps: number
  }
  threshold?: {
    avg: SpeechTempoThreshold
    std: SpeechTempoThreshold
  }
  metadata?: {
    assistant_speech_duration: number
    total_phonemes: number
    interval_size: number
  }
}

export class SpeechTempoMetricRecord extends WithAudioGateMixin(
  NumericalMetricRecord,
) {
  overviewSize = "medium" as const
  aggregate_units = "pps"

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const data = parseResultJson<SpeechTempoResultJson>(mo.result_json)
    const stats = data?.statistics
    const threshold = data?.threshold

    // Catch for if the metric doesn't have the new speech tempo structure
    if (
      !stats ||
      [stats.average_pps, stats.max_pps, stats.min_pps, stats.std_pps].some(
        (v) => v == null,
      )
    ) {
      return (
        <p className="text-sm text-gray-500">
          Results are outdated. Please rerun the metric to see the latest data.
        </p>
      )
    }

    const avgRanges = threshold?.avg ?? { warning: 16, danger: 30 }
    const stdRanges = threshold?.std ?? { warning: 4, danger: 10 }

    return (
      <div className="space-y-3">
        <div className="flex items-end gap-2">
          <h3 className="text-lg font-semibold">Phoneme Analysis</h3>
          <p className="text-sm text-muted-foreground">
            Phonemes per Second (pps)
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <ThresholdMetricBox
            title="Average PPS"
            value={stats.average_pps}
            ranges={avgRanges}
          />
          <ThresholdMetricBox
            title="Max PPS"
            value={stats.max_pps}
            ranges={avgRanges}
          />
          <ThresholdMetricBox title="Min PPS" value={stats.min_pps} />
          <ThresholdMetricBox
            title="Std Dev"
            value={stats.std_pps}
            ranges={stdRanges}
          />
        </div>
      </div>
    )
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (!mo) {
      return []
    }

    try {
      const data = parseResultJson<SpeechTempoResultJson>(mo.result_json)
      const warningThreshold = data?.threshold?.avg?.warning ?? 16
      const dangerThreshold = data?.threshold?.avg?.danger ?? 30

      const lowerWarningThreshold = 3

      const isWarningOrDanger = (pps: number) =>
        pps > warningThreshold || pps < lowerWarningThreshold

      const colorForPps = (pps: number) =>
        pps > dangerThreshold
          ? "rgba(229, 115, 115, 0.4)"
          : "rgba(240, 183, 117, 0.4)"

      // Use subvalues_by_timestamp (preferred source)
      // float_value = pps, start_offset/end_offset = interval bounds
      // Only show intervals that exceed warning/danger thresholds (too fast or too slow)
      const subvalues: SubvalueByTimestamp[] = mo.subvalues_by_timestamp ?? []
      if (subvalues && subvalues.length > 0) {
        return subvalues
          .filter((sv) => isWarningOrDanger(sv.float_value ?? 0))
          .map((sv) => {
            const pps = sv.float_value ?? 0
            return {
              id: `phoneme:${sv.start_offset}`,
              start: sv.start_offset,
              end: sv.end_offset,
              color: colorForPps(pps),
              drag: false,
              resize: false,
              content: `${pps.toFixed(1)} PPS`,
            }
          })
      }

      // @deprecated fallback: result_json.phonemes_by_interval — remove after subvalues_by_timestamp migration is complete
      const legacyData = parseResultJson<{
        phonemes_by_interval?: Record<
          string,
          { phoneme_count: number; speech_duration: number; start_time: number }
        >
      }>(mo.result_json)
      if (!legacyData?.phonemes_by_interval) {
        return []
      }
      return Object.entries(legacyData.phonemes_by_interval)
        .map(([_key, interval]) => {
          const pps =
            interval.speech_duration > 0
              ? interval.phoneme_count / interval.speech_duration
              : 0
          return { pps, interval }
        })
        .filter(({ pps }) => isWarningOrDanger(pps))
        .map(({ pps, interval }) => ({
          id: `phoneme:${interval.start_time}`,
          start: interval.start_time,
          end: interval.start_time + interval.speech_duration,
          color: colorForPps(pps),
          drag: false,
          resize: false,
          content: `${pps.toFixed(1)} PPS`,
        }))
    } catch (e) {
      log.debug("Phoneme interval data unavailable or could not parse", {
        error: e,
      })
    }

    return []
  }
}
